import { Money } from 'gportal-grpc/gportal/type/v1/money_pb';

/**
 * The credit figures of checkout step four, which the balance row and the
 * notes under it share. "Guthaben" is the paid-in balance plus the promotional
 * credit (TV-2194), so every figure here counts both, and the notes cannot
 * name a different balance than the row above them.
 */

/** GetOrderPrice's credit preview, the two credit lines of the price summary. */
export interface CreditPreview {
  promotionalCreditsApplied?: Money;
  balanceApplied?: Money;
}

function sum(a: Money, b: bigint): Money {
  return new Money({
    amount: BigInt(a.amount) + b,
    currencyCode: a.currencyCode,
    fraction: a.fraction,
  });
}

/**
 * The balance row's figure: what the credits can pay with, promotional credit
 * included. Promotional credit in another currency is left out.
 */
export function creditBalance(
  available: Money | null | undefined,
  promotionalCredits: Money | null | undefined,
): Money | null {
  if (!available) return null;
  if (
    !promotionalCredits ||
    promotionalCredits.currencyCode !== available.currencyCode
  ) {
    return available;
  }
  return sum(available, BigInt(promotionalCredits.amount));
}

/**
 * The promotional credit this purchase uses, or null without a preview in the
 * order's currency.
 */
export function promotionalDeduction(
  total: Money | null | undefined,
  preview: CreditPreview | null | undefined,
): Money | null {
  const applied = preview?.promotionalCreditsApplied;
  if (!total || !applied || applied.currencyCode !== total.currencyCode) {
    return null;
  }
  return applied;
}

/**
 * What the credits pay of this purchase: Chargebee takes the promotional
 * credit off the invoice first and the balance pays what it leaves. Without a
 * preview in the order's currency, the whole total.
 */
export function creditDeduction(
  total: Money | null | undefined,
  preview: CreditPreview | null | undefined,
): Money | null {
  if (!total) return null;
  const applied = preview?.balanceApplied;
  if (!applied || applied.currencyCode !== total.currencyCode) return total;
  const promotional = promotionalDeduction(total, preview);
  return sum(applied, promotional ? BigInt(promotional.amount) : 0n);
}

/** What is left of the balance row's figure after this purchase. */
export function remainingCredit(
  balance: Money | null,
  deduction: Money | null,
): Money | null {
  if (!balance || !deduction) return null;
  return sum(balance, -BigInt(deduction.amount));
}
